import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { SearchFacet, SearchTabItem } from './../../../types/search';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useSearchConfig } from '../../Search/hooks/useSearchConfig';

export const useDocumentListing = (
  apiUrl: string,
  paginationDisplayLimit: number = 10,
  documentContentTypes?: string,
  prefilterCategoryIds: string[] = [],
  facetKeyForCategories: string = 'pdfcategoryids',
  listingType: string = 'documents'
) => {
  const config = useSearchConfig();
  const initialFacetItemsRef = useRef<Record<string, Array<{ value: string; label?: string }>>>({});
  const dateFacetKeysRef = useRef<Set<string>>(new Set());

  // Core state
  const [searchParams, setSearchParams] = useState({
    page: 1,
    sortOrder: 'MostRecent',
    activeTab: listingType as string | null,
    filters: {} as Record<string, string[]>,
  });

  // UI state
  const [openFacets, setOpenFacets] = useState<Record<string, boolean>>({});
  const [previousResults, setPreviousResults] = useState<any[]>([]);
  const [previousTabs, setPreviousTabs] = useState<any[]>([]);
  const [previousFacets, setPreviousFacets] = useState<any[]>([]);
  const [urlParamsLoaded, setUrlParamsLoaded] = useState(false);

  const prefilterStrings = useMemo(() => prefilterCategoryIds.map(String), [prefilterCategoryIds]);

  const buildQueryString = useCallback(
    (params: typeof searchParams) => {
      const urlParams = new URLSearchParams();

      urlParams.set('page', params.page.toString());
      urlParams.set('order', params.sortOrder);
      urlParams.set('type', 'document');
      urlParams.set('pagetype', encodeURIComponent(params.activeTab || listingType));

      if (documentContentTypes && config.documentAssetTypeGuid) {
        urlParams.set(config.documentAssetTypeGuid, documentContentTypes);
      }

      const effective: Record<string, string[]> = JSON.parse(JSON.stringify(params.filters ?? {}));

      const userSelected = effective[facetKeyForCategories] ?? [];
      if (prefilterStrings.length > 0) {
        if (userSelected.length > 0) {
          const allowed = new Set(prefilterStrings);
          effective[facetKeyForCategories] = userSelected.filter(v => allowed.has(v));
        } else {
          effective[facetKeyForCategories] = [...prefilterStrings];
        }
      }

      Object.entries(effective).forEach(([key, values]) => {
        if (Array.isArray(values) && values.length > 0) {
          urlParams.set(key, values.join(','));
        }
      });

      return `?${urlParams.toString()}`;
    },
    [documentContentTypes, config.documentAssetTypeGuid, prefilterStrings, listingType, facetKeyForCategories]
  );

  const updateUrl = useCallback(
    (params: typeof searchParams) => {
      const queryString = buildQueryString(params);
      globalThis.history.pushState({}, '', queryString);
    },
    [buildQueryString]
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(globalThis.location.search);
    const initialParams = { ...searchParams };

    const urlPage = urlParams.get('page');
    if (urlPage) initialParams.page = Number.parseInt(urlPage, 10);
    const urlSort = urlParams.get('order');
    if (urlSort) initialParams.sortOrder = urlSort;

    const urlFilters: Record<string, string[]> = {};
    urlParams.forEach((value, key) => {
      if (!['page', 'order', 'type'].includes(key) && key !== config.documentAssetTypeGuid) {
        urlFilters[key] = value.split(',');
      }
    });
    initialParams.filters = urlFilters;

    setSearchParams(initialParams);
    setUrlParamsLoaded(true);
  }, [config.documentAssetTypeGuid, searchParams]);

  const listingQuery = useQuery({
    queryKey: [
      'documentsListing',
      {
        page: searchParams.page,
        sort: searchParams.sortOrder,
        filters: JSON.stringify(searchParams.filters),
      },
    ],
    queryFn: async () => {
      const queryString = buildQueryString(searchParams);
      const apiParams = new URLSearchParams(queryString.substring(1));
      apiParams.set('pageSize', paginationDisplayLimit.toString());
      const apiQueryString = `?${apiParams.toString()}`;

      const url = `${apiUrl}${apiQueryString}`;
      const response = await fetch(url);

      if (!response.ok) throw new Error('Document listing fetch failed');
      return await response.json();
    },
    staleTime: 60000,
    enabled: urlParamsLoaded,
    placeholderData: keepPreviousData,
  });

  const trackDateFacet = useCallback((facet: SearchFacet) => {
    if (facet.type === 'date') {
      dateFacetKeysRef.current.add(facet.key);
    }
  }, []);

  const buildFacetMap = useCallback(
    (facets: SearchFacet[]) => {
      const map: Record<string, Array<{ value: string; label?: string }>> = {};
      facets.forEach(facet => {
        map[facet.key] = (facet.items ?? []).map(item => ({
          value: String(item.value),
          label: item.label,
        }));
        trackDateFacet(facet);
      });
      return map;
    },
    [trackDateFacet]
  );

  const initializeFacets = useCallback(
    (facets: SearchFacet[]) => {
      initialFacetItemsRef.current = buildFacetMap(facets);
    },
    [buildFacetMap]
  );

  const shouldInitializeFacets = useCallback(() => {
    return (
      !listingQuery.isLoading && listingQuery.data?.facets && Object.keys(initialFacetItemsRef.current).length === 0
    );
  }, [listingQuery.isLoading, listingQuery.data?.facets]);

  const updatePreviousData = useCallback(() => {
    if (listingQuery.isLoading) return;

    if (listingQuery.data?.results) {
      setPreviousResults(listingQuery.data.results);
    }
    if (listingQuery.data?.tabs) {
      setPreviousTabs(listingQuery.data.tabs);
    }
    if (listingQuery.data?.facets) {
      setPreviousFacets(listingQuery.data.facets);
    }
  }, [listingQuery.isLoading, listingQuery.data?.results, listingQuery.data?.tabs, listingQuery.data?.facets]);

  useEffect(() => {
    if (shouldInitializeFacets()) {
      initializeFacets(listingQuery.data.facets as SearchFacet[]);
    }

    updatePreviousData();
  }, [
    listingQuery.isLoading,
    listingQuery.data?.facets,
    listingQuery.data?.results,
    listingQuery.data?.tabs,
    initializeFacets,
    shouldInitializeFacets,
    updatePreviousData,
  ]);

  const facets = useMemo(() => {
    const source = listingQuery.data?.facets ?? previousFacets;
    if (!source) return [];

    return source
      .filter((facet: SearchFacet) => facet.key !== 'tab')
      .map((facet: SearchFacet) => {
        const currentByValue = new Map((facet.items ?? []).map(i => [String(i.value), i]));
        const baseline =
          initialFacetItemsRef.current[facet.key] ??
          (facet.items ?? []).map(i => ({ value: String(i.value), label: i.label }));

        const mergedItems = baseline.map(b => {
          const curr = currentByValue.get(b.value);
          return {
            ...(curr ?? { value: b.value, label: b.label, total: 0 }),
            isSelected: (searchParams.filters[facet.key] ?? []).includes(b.value),
          };
        });

        return { ...facet, isOpen: openFacets[facet.key] || false, items: mergedItems };
      });
  }, [listingQuery.data?.facets, previousFacets, openFacets, searchParams.filters]);

  const facetsWithOpenState = useMemo(() => {
    return facets.map((facet: SearchFacet) => ({
      ...facet,
      isOpen: openFacets[facet.key] || false,
      items: (facet.items || []).map(item => ({
        ...item,
        isSelected: searchParams.filters[facet.key]?.includes(item.value) || false,
      })),
    }));
  }, [facets, openFacets, searchParams.filters]);

  const appliedFacetsWithState = useMemo(() => {
    return facets.map((facet: SearchFacet) => ({
      ...facet,
      items: (facet.items || []).map(item => ({
        ...item,
        isSelected: searchParams.filters[facet.key]?.includes(item.value) || false,
      })),
    }));
  }, [facets, searchParams.filters]);

  // Action handlers
  const toggleFacet = useCallback((facetKey: string) => {
    setOpenFacets(prev => {
      const isCurrentlyOpen = prev[facetKey];

      if (isCurrentlyOpen) {
        return { ...prev, [facetKey]: false };
      }

      const newState: Record<string, boolean> = {};
      Object.keys(prev).forEach(key => {
        newState[key] = false;
      });

      newState[facetKey] = true;
      return newState;
    });
  }, []);

  const toggleFilter = useCallback(
    (facetKey: string, itemValue: string) => {
      setSearchParams(prev => {
        const newFilters = { ...prev.filters };
        const values = [...(newFilters[facetKey] ?? [])];
        const i = values.indexOf(itemValue);
        if (i >= 0) values.splice(i, 1);
        else values.push(itemValue);
        if (values.length) newFilters[facetKey] = values;
        else delete newFilters[facetKey];

        const next = { ...prev, filters: newFilters, page: 1 };
        updateUrl(next); // updates URL
        return next; // triggers React Query refetch via queryKey
      });
    },
    [updateUrl]
  );

  const clearAllFilters = useCallback(() => {
    const emptyFilters = {};

    const newParams = {
      ...searchParams,
      filters: emptyFilters,
      page: 1,
    };

    setSearchParams(newParams);

    const urlParams = new URLSearchParams();
    urlParams.set('page', '1');
    urlParams.set('order', newParams.sortOrder);
    urlParams.set('type', 'document');

    if (documentContentTypes && config.documentAssetTypeGuid) {
      urlParams.set(config.documentAssetTypeGuid, documentContentTypes);
    }

    globalThis.history.pushState({}, '', `?${urlParams.toString()}`);

    listingQuery.refetch();
  }, [searchParams, listingQuery, documentContentTypes, config.documentAssetTypeGuid]);

  const handlePageChange = useCallback(
    (page: number) => {
      const newParams = { ...searchParams, page };
      setSearchParams(newParams);
      updateUrl(newParams);
    },
    [searchParams, updateUrl]
  );

  const handleSortChange = useCallback(
    (newSortOrder: string) => {
      const newParams = { ...searchParams, sortOrder: newSortOrder };
      setSearchParams(newParams);
      updateUrl(newParams);
    },
    [searchParams, updateUrl]
  );

  const removeFilter = useCallback(
    (facetKey: string, itemValue: string) => {
      const newFilters = JSON.parse(JSON.stringify(searchParams.filters));

      if (newFilters[facetKey]) {
        const values = newFilters[facetKey].filter((v: string) => v !== itemValue);
        if (values.length) {
          newFilters[facetKey] = values;
        } else {
          delete newFilters[facetKey];
        }
      }

      const newParams = {
        ...searchParams,
        filters: newFilters,
        page: 1,
      };

      setSearchParams(newParams);

      const urlParams = new URLSearchParams();
      urlParams.set('page', '1');
      urlParams.set('order', newParams.sortOrder);
      urlParams.set('type', 'document');

      if (documentContentTypes && config.documentAssetTypeGuid) {
        urlParams.set(config.documentAssetTypeGuid, documentContentTypes);
      }

      Object.entries(newFilters).forEach(([key, values]) => {
        if (Array.isArray(values) && values.length > 0) {
          urlParams.set(key, values.join(','));
        }
      });

      globalThis.history.pushState({}, '', `?${urlParams.toString()}`);
    },
    [searchParams, documentContentTypes, config.documentAssetTypeGuid]
  );

  const tabsFromApi = useMemo(() => {
    return listingQuery.data?.tabs ?? listingQuery.data?.summaryTabs ?? [];
  }, [listingQuery.data?.tabs, listingQuery.data?.summaryTabs]);

  const sortOptions = useMemo(() => {
    if (tabsFromApi.length > 0) {
      const currentTab = tabsFromApi.find(
        (tab: any) => tab.isSelected === true || tab.key === (searchParams.activeTab ?? 'documents')
      );
      if (currentTab?.sortOptions?.length) return currentTab.sortOptions;
    }
    return listingQuery.data?.sortOptions ?? [];
  }, [tabsFromApi, listingQuery.data?.sortOptions, searchParams.activeTab]);

  const tabs = useMemo(() => {
    if (!listingQuery.data?.tabs && previousTabs.length > 0) {
      return previousTabs.map((tab: SearchTabItem) => ({
        ...tab,
        isSelected: tab.key === searchParams.activeTab,
      }));
    }

    if (!listingQuery.data?.tabs) return [];

    return listingQuery.data.tabs.map((tab: SearchTabItem) => ({
      ...tab,
      isSelected: tab.key === searchParams.activeTab,
    }));
  }, [listingQuery.data?.tabs, searchParams.activeTab, previousTabs]);

  const filteredResults = useMemo(() => {
    if (listingQuery.isLoading && previousResults.length > 0) {
      return previousResults;
    }

    if (listingQuery.data?.results) {
      return listingQuery.data.results;
    }

    return [];
  }, [listingQuery.data?.results, listingQuery.isLoading, previousResults]);

  return {
    // Core state and derived state
    results: filteredResults,
    activeTabKey: searchParams.activeTab,
    sortOrder: searchParams.sortOrder,

    // React Query states
    isLoading: listingQuery.isLoading,
    isError: listingQuery.isError,
    isSuccess: listingQuery.isSuccess,
    isFetching: listingQuery.isFetching,

    // UI state
    facets: facetsWithOpenState,
    appliedFilters: appliedFacetsWithState,
    tabs,
    sortOptions,
    pagination: {
      currentPage: searchParams.page,
      totalPages: listingQuery.data?.pagination?.totalPages ?? 1,
      totalResults: listingQuery.data?.pagination?.totalResults,
    },

    // Actions
    toggleFacet,
    toggleFilter,
    removeFilter,
    clearAllFilters,
    handlePageChange,
    handleSortChange,
  };
};
